import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { isPointWithinElement } from '@theme/utilities';

const OKENDO_OVERLAY_SELECTOR = '.oke-modal, [data-oke-modal]';
const OUTSIDE_CLICK_GUARD_MS = 500;

/**
 * Widget Plus window API.
 * https://docs.okendo.io/on-site/storefront-javascript-api/widget-plus-window-api
 *
 * @typedef {object} OkendoWidgetApi
 * @property {(widgetElement: HTMLElement, forceReinitialization?: boolean) => void} [initWidget]
 * @property {() => void} [initAllWidgets]
 */

/**
 * @typedef {Window & {
 *   okeWidgetApi?: OkendoWidgetApi;
 * }} OkendoWindow
 */

/**
 * The widget starts inside a closed dialog, so Okendo's page-load scan can miss it.
 * Re-init with their documented window API once the drawer is open.
 *
 * @param {ParentNode} root
 * @returns {boolean}
 */
function initOkendoWidget(root) {
  const widget = root.querySelector('[data-oke-widget]');
  if (!(widget instanceof HTMLElement)) return true;

  const api = /** @type {OkendoWindow} */ (window).okeWidgetApi;
  if (!api) return false;

  if (typeof api.initWidget === 'function') {
    api.initWidget(widget, true);
    return true;
  }

  if (typeof api.initAllWidgets === 'function') {
    api.initAllWidgets();
    return true;
  }

  return false;
}

/**
 * Figma bars are four segments with a square marker. Okendo draws one continuous track.
 * @param {ParentNode} root
 */
/**
 * @param {HTMLElement} bar
 * @returns {number}
 */
function scaleGap(bar) {
  const raw = getComputedStyle(bar).getPropertyValue('--bt-reviews-seg-gap');
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : 4;
}

function paintAttributeBars(root) {
  for (const bar of root.querySelectorAll('.oke-barClassic')) {
    if (!(bar instanceof HTMLElement)) continue;
    const existing = bar.querySelector('.bt-reviews-scale');
    if (existing instanceof HTMLElement) {
      if (existing.dataset.measured === 'true' || bar.getBoundingClientRect().width <= 0) continue;
      existing.remove();
    }
    const dot = bar.querySelector('.oke-barClassic-marker-dot');
    let pct = 50;
    if (dot instanceof HTMLElement) {
      const leftStyle = dot.style.left;
      if (leftStyle.includes('%')) {
        pct = Number.parseFloat(leftStyle);
      } else {
        const barRect = bar.getBoundingClientRect();
        const dotRect = dot.getBoundingClientRect();
        if (barRect.width > 0) {
          pct = ((dotRect.left + dotRect.width / 2 - barRect.left) / barRect.width) * 100;
        }
      }
    }
    pct = Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 50));

    const visual = document.createElement('span');
    visual.className = 'bt-reviews-scale';
    visual.setAttribute('aria-hidden', 'true');
    const segmentCount = 4;
    const gap = scaleGap(bar);
    const width = bar.getBoundingClientRect().width;
    const segmentWidth = width > 0 ? (width - gap * (segmentCount - 1)) / segmentCount : 0;
    const markerX = (pct / 100) * width;

    for (let index = 0; index < segmentCount; index += 1) {
      const segment = document.createElement('span');
      segment.className = 'bt-reviews-scale__segment';
      const start = segmentWidth > 0 ? index * (segmentWidth + gap) : index * 25;
      const end = segmentWidth > 0 ? start + segmentWidth : start + 25;
      const position = segmentWidth > 0 ? markerX : pct;
      if (position >= end - 0.5) {
        segment.classList.add('is-filled');
      } else if (position > start) {
        const portion = ((position - start) / (end - start)) * 100;
        segment.classList.add('is-partial');
        segment.style.setProperty('--bt-reviews-fill', `${portion}%`);
      }
      visual.append(segment);
    }
    if (segmentWidth > 0) visual.dataset.measured = 'true';
    const marker = document.createElement('span');
    marker.className = 'bt-reviews-scale__marker';
    marker.style.left = `${pct}%`;
    visual.append(marker);
    bar.append(visual);
  }
}

/**
 * PDP reviews drawer — right-slide on all breakpoints. Hosts Okendo’s widget.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [body]
 * @property {HTMLDialogElement} [photoDialog]
 * @property {HTMLImageElement} [photoImage]
 *
 * @extends {DialogComponent}
 */
class BtReviewsDrawer extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #overlayObserver = null;

  /** @type {number} */
  #initTimer = 0;

  /** @type {number} */
  #ignoreOutsideUntil = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('click', this.#onHostClick, { signal });
    this.addEventListener('click', this.#onReadMoreAnchor, { signal, capture: true });

    const root = this.refs.body ?? this;
    root.addEventListener('click', this.#onReviewPhotoClick, { signal, capture: true });

    const photoDialog = this.refs.photoDialog;
    photoDialog?.addEventListener('click', this.#onPhotoDialogClick, { signal });
    photoDialog?.addEventListener('keydown', this.#onPhotoDialogKeyDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopInitRetry();
    this.#stopOverlayObserver();
    this.#restoreAdoptedOverlays();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #onDialogOpen = () => {
    const root = this.refs.body ?? this;
    this.#stopInitRetry();
    const paint = () => paintAttributeBars(root);
    if (initOkendoWidget(root)) {
      paint();
    } else {
      let attempts = 0;
      this.#initTimer = window.setInterval(() => {
        attempts += 1;
        if (initOkendoWidget(root) || attempts >= 20) {
          paint();
          this.#stopInitRetry();
        }
      }, 250);
    }
    window.setTimeout(paint, 300);
    this.#adoptExistingOverlays();
    this.#startOverlayObserver();
  };

  #onDialogClose = () => {
    this.#closePhoto();
    this.#stopInitRetry();
    this.#stopOverlayObserver();
    this.#restoreAdoptedOverlays();
    this.#ignoreOutsideUntil = 0;
  };

  /**
   * Remember taps that start on widget content (not the backdrop).
   * Okendo's "read more" replaces that node before click bubbles.
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const target = event.target;
    if (!(target instanceof Node) || target === dialog) return;
    if (!dialog.contains(target)) return;

    this.#ignoreOutsideUntil = performance.now() + OUTSIDE_CLICK_GUARD_MS;
  };

  /**
   * Keep hash-style "read more" links from navigating the page.
   * @param {MouseEvent} event
   */
  #onReadMoreAnchor = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('.oke-readMore');
    if (!(trigger instanceof HTMLAnchorElement)) return;

    event.preventDefault();
  };

  /**
   * DialogComponent closes on click-outside. After Okendo expands the
   * summary, the original target is detached (or iOS retargets the tap
   * onto the backdrop). Don't treat that as a dismiss.
   * @param {MouseEvent} event
   */
  #onHostClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const target = event.target;
    const contained = target instanceof Node && dialog.contains(target);
    const pointInside = isPointWithinElement(event.clientX, event.clientY, dialog);
    const detached = target instanceof Node && !document.contains(target);
    const recentlyInside = performance.now() < this.#ignoreOutsideUntil;
    const looksOutside = target instanceof HTMLDialogElement ? !pointInside : !contained;

    if (looksOutside && (recentlyInside || (detached && pointInside))) {
      event.stopImmediatePropagation();
    }
  };

  /**
   * Okendo's photo click opens its lightbox on document.body, behind this dialog.
   * Show the clicked image in our own dialog instead.
   * @param {Event} event
   */
  #onReviewPhotoClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('.oke-media-link, .oke-media-image');
    if (!(trigger instanceof HTMLElement)) return;
    if (!this.contains(trigger)) return;

    const source = trigger instanceof HTMLImageElement ? trigger : trigger.querySelector('img');
    if (!(source instanceof HTMLImageElement)) return;

    const src = reviewPhotoSource(source);
    if (!src) return;

    event.preventDefault();
    event.stopPropagation();

    const photoDialog = this.refs.photoDialog;
    const photoImage = this.refs.photoImage;
    if (!(photoDialog instanceof HTMLDialogElement) || !(photoImage instanceof HTMLImageElement)) return;

    photoImage.alt = source.alt || photoDialog.getAttribute('aria-label') || '';
    photoImage.style.width = '';
    photoImage.style.height = '';
    photoImage.onload = () => fitReviewPhoto(photoImage);
    photoImage.src = src;
    if (photoImage.complete) fitReviewPhoto(photoImage);
    if (!photoDialog.open) photoDialog.showModal();
  };

  /**
   * Backdrop and the close button dismiss the photo only.
   * @param {MouseEvent} event
   */
  #onPhotoDialogClick = (event) => {
    const photoDialog = this.refs.photoDialog;
    if (!(photoDialog instanceof HTMLDialogElement)) return;

    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target === photoDialog || target.closest('[data-bt-reviews-photo-close]')) {
      this.#closePhoto();
    }
  };

  /**
   * Escape closes the photo. Stop it reaching the drawer, which also listens for Escape.
   * @param {KeyboardEvent} event
   */
  #onPhotoDialogKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.#closePhoto();
  };

  #closePhoto() {
    const photoDialog = this.refs.photoDialog;
    if (photoDialog instanceof HTMLDialogElement && photoDialog.open) {
      photoDialog.close();
    }
  }

  #stopInitRetry() {
    window.clearInterval(this.#initTimer);
    this.#initTimer = 0;
  }

  #startOverlayObserver() {
    if (this.#overlayObserver || typeof MutationObserver === 'undefined') return;
    this.#overlayObserver = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          this.#adoptOverlay(node);
        }
      }
      paintAttributeBars(this.refs.body ?? this);
    });
    this.#overlayObserver.observe(document.body, { childList: true, subtree: true });
  }

  #stopOverlayObserver() {
    this.#overlayObserver?.disconnect();
    this.#overlayObserver = null;
  }

  #adoptExistingOverlays() {
    for (const node of document.querySelectorAll(OKENDO_OVERLAY_SELECTOR)) {
      this.#adoptOverlay(node);
    }
  }

  /**
   * Native `<dialog>` is top-layer; Okendo appends modals to `document.body`.
   * Reparent so write-review / photo lightbox sit above the drawer.
   * @param {Node} node
   */
  #adoptOverlay(node) {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const overlay =
      node instanceof Element
        ? node.matches(OKENDO_OVERLAY_SELECTOR)
          ? node
          : node.querySelector?.(OKENDO_OVERLAY_SELECTOR)
        : null;
    if (!(overlay instanceof HTMLElement)) return;
    if (dialog.contains(overlay)) return;

    dialog.append(overlay);
    overlay.setAttribute('data-bt-reviews-adopted', '');
  }

  #restoreAdoptedOverlays() {
    for (const overlay of this.querySelectorAll('[data-bt-reviews-adopted]')) {
      document.body.append(overlay);
      overlay.removeAttribute('data-bt-reviews-adopted');
    }
  }
}

/**
 * Scale the photo up to 80% of the viewport, keeping its aspect ratio.
 * @param {HTMLImageElement} image
 */
function fitReviewPhoto(image) {
  const { naturalWidth, naturalHeight } = image;
  if (!naturalWidth || !naturalHeight) return;

  const maxWidth = window.innerWidth * 0.8;
  const maxHeight = window.innerHeight * 0.8;
  const ratio = naturalWidth / naturalHeight;
  let height = maxHeight;
  let width = height * ratio;
  if (width > maxWidth) {
    width = maxWidth;
    height = width / ratio;
  }

  image.style.width = `${Math.round(width)}px`;
  image.style.height = `${Math.round(height)}px`;
}

/**
 * Thumbnails request Okendo's 200px rendition (`?d=x200`).
 * Their lightbox asks for `?d=1600x1600` from the same file.
 * @param {HTMLImageElement} image
 * @returns {string}
 */
function reviewPhotoSource(image) {
  const src = image.currentSrc || image.getAttribute('src') || '';
  if (!src) return '';

  try {
    const url = new URL(src, window.location.href);
    if (url.hostname.endsWith('okendo.io') && url.searchParams.has('d')) {
      url.searchParams.set('d', '1600x1600');
      url.searchParams.delete('crop');
      return url.toString();
    }
  } catch {
    /* Keep the thumbnail URL. */
  }

  return src;
}

if (!customElements.get('bt-reviews-drawer')) {
  customElements.define('bt-reviews-drawer', BtReviewsDrawer);
}
